import { Component, HostListener, ViewChild } from '@angular/core';
import { DxValidationGroupComponent } from 'devextreme-angular';
import { BaseState, LoginParams } from '@base/entities/BaseEntities';
import { BasePageComponent } from '@base/component/BasePageComponent';
import { ServiceBagService, ServiceBagServices } from '@base/component/ServiceBag';
import { SysGlobals } from '@core/framework/sysglobals';
import { Utils } from '@core/helpers/Utils';
import { EnumLanguages, EnumLoginExtensionLinkType, EnumLoginSecureKeyTypes, EnumNotificationDeliveryOptions } from '@core/framework/sysenums.enum';
import { EnumSystemOptions } from '@core/framework/systemoptions.enum';
import { LoginService } from '@core/framework/login.service';
import { LoginExtensionLink } from '@base/classes/BaseClasses';
import { VersionDescriptor } from '@m4u-angular/configuration/app.config.service';
import { SessionService } from '@m4u-angular/core/framework/session.service';
import { LanguageService } from '@m4u-angular/core/framework/language.service';
import { Subscription } from 'rxjs';

class State extends BaseState {
    //IsMultiLanguage: boolean;
    IsSaveCredentials: boolean = false;
    ShowPasswordRecoveryLink: boolean;
    LoginExtensionLinks: LoginExtensionLink[];
    ReturnUrl: string;
    ReturnUrlParams: string;
    IsSupportedLoginByOTP: boolean;
    Stage: number;
    ModulesVersion: VersionDescriptor[] = [];
}

@Component({
    providers: ServiceBagServices,
    selector: 'm4u-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent extends BasePageComponent<LoginParams, State>
{
    @HostListener('document:keypress', ['$event'])
    EnterPress(event: KeyboardEvent) { if (event.key === 'Enter') { this.OnSubmit(); } }

    @ViewChild('validationGroup') protected validationGroup: DxValidationGroupComponent;

    private _subscriptions: Subscription[] = [];

    constructor(A: ServiceBagService,
        private loginService: LoginService,
        private languageService: LanguageService) {
        super(A, 'LoginComponent', LoginParams, State);
    }

    override OnInitComponent() {
        //Checking support of the login by otp is needed for to avoid an endless loop of transitions between 'login' & login-otp'
        if (!this.SystemOptionFlag(EnumSystemOptions.IsSupportedLoginByPassword) && this.SystemOptionFlag(EnumSystemOptions.IsSupportedLoginByOTP)) {
            this.Navigate(["/system/login-otp"], { Language: SysGlobals.Language });
            return;
        }

        this.State.Stage = 1;
        this.Entity.SecureKeyType = EnumLoginSecureKeyTypes.Password;

        this.State.ModulesVersion = this.A.AppConfigService.getModulesVersion();

        this._subscriptions.push(this.languageService.Language$.subscribe(language => {
            if (this.Entity.Language === language) return;
            
            this.Entity.Language = language;
            this.A.Navigator.Navigate(["/system/login"], { Language: SysGlobals.Language });
            setTimeout(() => this.A.Navigator.ReloadApplication(), 500);
        }));
    }

    override OnInitState() {
        this.State.ShowPasswordRecoveryLink = this.SystemOptionFlag(EnumSystemOptions.IsSupportedPasswordRecovery);
        this.State.IsSupportedLoginByOTP = this.SystemOptionFlag(EnumSystemOptions.IsSupportedLoginByOTP);
        this.State.ReturnUrl = this.QueryParams.ReturnUrl
            && (this.QueryParams.ReturnUrl as string).replace(this.A.AppConfigService.M4UConfig.deploymentApplicationFolder, '');
        this.State.ReturnUrlParams = this.QueryParams.ReturnUrlParams;
    }

    override OnInitEntity() {
        //Take default language from URL to allow presetting it
        //this.State.IsMultiLanguage = this.A.AppConfigService.M4UConfig.isMultiLanguage;
        if (this.QueryParams.Language != undefined) {
            this.Entity.Language = (this.QueryParams.Language == EnumLanguages.English ? EnumLanguages.English : EnumLanguages.Hebrew);
        }
        else {
            const language = localStorage.getItem('Language');
            this.Entity.Language = language || this.A.AppConfigService.M4UConfig.defaultLanguage;
        }
        this.languageService.SetLanguage(this.Entity.Language);

        //Take sure if was passed in
        this.Entity.UserName = this.QueryParams.UserName;
        this.LoadExtension();
    }

    //Perform the login
    OnSubmit() {
        if (!this.validationGroup.instance.validate().isValid) return;
        this.loginService.DoLogin(this.Entity, false, this.State.IsSaveCredentials, () => { this.OnAfterInit(); }, null, (deliveryOption: EnumNotificationDeliveryOptions) => { this.MFACodeSent(deliveryOption) })
            .subscribe(() => {});
    }

    OnAfterInit() {
        if (!!this.State.ReturnUrl) {
            var params = this.State.ReturnUrlParams ? JSON.parse(this.State.ReturnUrlParams) : undefined;
            this.A.Navigator.Navigate([this.State.ReturnUrl], params);
            return;
        }
        this.A.Framework.NavigateDefault();
    }

    override OnSubmitPage() {
        this.OnSubmit();
    }

    OnPasswordRecovery() {
        this.Navigate(['/system/passwordrecovery'], { UserName: this.Entity.UserName })
    }

    LoadExtension() {
        this.A.AppConfigService.AppConfig.OnGetAppLoginExtensionLinks()
            .subscribe(links => this.State.LoginExtensionLinks = links);
    }

    OnExtensionLinkClick(link: LoginExtensionLink) {
        (link.Type === EnumLoginExtensionLinkType.Route || link.Type === EnumLoginExtensionLinkType.ButtonLink) && this.Navigate([link.Link]);
    }

    GoToLoginByOTP() {
        if (this.State.IsSupportedLoginByOTP) this.Navigate(['/system/login-otp'], { Language: this.Entity.Language });
    }

    MFACodeSent(deliveryOption: EnumNotificationDeliveryOptions) {
        this.Entity.DeliveryOption = deliveryOption;
        this.State.Stage = 2;
        this.State.IsSaveCredentials = false;
        this.Entity.SecureKey = null;
        this.Entity.SecureKeyType = EnumLoginSecureKeyTypes.MFACode;

        this.UI.InfoMessage(this.Entity.DeliveryOption == EnumNotificationDeliveryOptions.Email ? this.RC('titleMFACodeOnEmailSent') : this.RC('titleMFACodeOnPhoneSent'))
    }

    GetAdditionalTitle() {
        if(this.State.Stage == 2) {
            if(this.Entity.DeliveryOption == EnumNotificationDeliveryOptions.Email) return this.RC('titleMFACodeOnEmailSent');
            else return this.RC('titleMFACodeOnPhoneSent');
        }
        return '';
    }

    OnCodeFieldInitialized(e){
        setTimeout(() => e.component.instance().focus(), 500);
    }

    protected override OnDestroyComponent(): void {
        this._subscriptions.forEach(x => x.unsubscribe());
    }
}
